import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Phone, MessageCircle, Send, Loader2, Calendar, Clock
} from 'lucide-react';

const NewComplaint = () => {
  const [formData, setFormData] = useState({
    student_name: '',
    phone: '',
    problem_type: '',
    remarks: '',
    source: 'calling',
    timeline: ''
  });
  const [problemTypes, setProblemTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const user = authService.getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProblemTypes();
  }, []);

  const fetchProblemTypes = async () => {
    setLoading(true);
    try {
      const response = await api.getProblemTypes();
      setProblemTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch problem types');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.student_name.trim()) {
      toast.error('Please enter student name');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter phone number');
      return;
    }
    if (!formData.problem_type) {
      toast.error('Please select problem type');
      return;
    }
    if (!formData.remarks.trim()) {
      toast.error('Please enter remarks');
      return;
    }
    if (!formData.timeline) {
      toast.error('Please set timeline for resolution');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.createComplaint({
        ...formData,
        timeline: new Date(formData.timeline).toISOString()
      });
      toast.success(`Complaint created! Ticket: ${response.data.ticket_number}`);
      navigate('/complaints/list');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create complaint');
    } finally {
      setSubmitting(false);
    }
  };

  // Set minimum datetime to now
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  return (
    <div data-testid="new-complaint-page" className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/complaints/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold font-headings text-slate-900">New Complaint</h1>
        <p className="text-slate-600 font-body">Register a new customer complaint</p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Source Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Complaint Source *
            </label>
            <div className="flex gap-4">
              <label
                className={`flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  formData.source === 'calling'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="source"
                  value="calling"
                  checked={formData.source === 'calling'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <Phone size={24} />
                <span className="font-medium">Calling Team</span>
              </label>
              <label
                className={`flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  formData.source === 'whatsapp'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="source"
                  value="whatsapp"
                  checked={formData.source === 'whatsapp'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <MessageCircle size={24} />
                <span className="font-medium">WhatsApp Team</span>
              </label>
            </div>
          </div>

          {/* Student Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Student Name *
              </label>
              <input
                type="text"
                name="student_name"
                value={formData.student_name}
                onChange={handleChange}
                placeholder="Enter student's full name"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="student-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="phone-input"
              />
            </div>
          </div>

          {/* Problem Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Problem Type *
            </label>
            <select
              name="problem_type"
              value={formData.problem_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="problem-type-select"
            >
              <option value="">Select problem type...</option>
              {problemTypes.map(pt => (
                <option key={pt.id} value={pt.name}>{pt.name}</option>
              ))}
            </select>
            {problemTypes.length === 0 && !loading && (
              <p className="text-sm text-amber-600 mt-1">
                No problem types available. Admin needs to add them first.
              </p>
            )}
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Problem Description / Remarks *
            </label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              placeholder="Describe the problem in detail..."
              rows={4}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
              data-testid="remarks-input"
            />
          </div>

          {/* Timeline */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <div className="flex items-center gap-2">
                <Clock size={16} />
                Resolution Timeline (Deadline) *
              </div>
            </label>
            <input
              type="datetime-local"
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              min={getMinDateTime()}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="timeline-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Set the deadline by which you commit to resolve this complaint
            </p>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => navigate('/complaints/dashboard')}
              className="px-6 py-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-complaint-btn"
              className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Send size={20} />
              )}
              {submitting ? 'Submitting...' : 'Submit Complaint'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewComplaint;
